// Utility functions for lead card display and formatting

/**
 * Format a date/time into a human-readable "time ago" string
 */
export const formatTimeAgo = (date: string | Date): string => {
  const now = new Date();
  const then = new Date(date);
  const diffInMs = now.getTime() - then.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    if (diffInHours === 0) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      if (diffInMinutes < 5) return 'Just now';
      return `${diffInMinutes} min ago`;
    }
    if (diffInHours === 1) return '1 hour ago';
    return `${diffInHours} hours ago`;
  }
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  
  const weeks = Math.floor(diffInDays / 7);
  if (diffInDays < 30) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  
  const months = Math.floor(diffInDays / 30);
  if (diffInDays < 365) return `${months} month${months !== 1 ? 's' : ''} ago`;
  
  const years = Math.floor(diffInDays / 365);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
};

/**
 * Format deal value as currency
 */
export const formatDealValue = (value: number | null | undefined): string => {
  if (!value) return 'No value';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Get initials from a name
 */
export const getInitials = (name: string): string => {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Get lead quality color classes based on score
 */
export const getLeadQualityColor = (score: number | null | undefined): string => {
  const scoreValue = score || 0;
  if (scoreValue >= 5) return 'from-green-500 to-green-600';
  if (scoreValue >= 4) return 'from-green-400 to-green-500';
  if (scoreValue >= 3) return 'from-blue-400 to-blue-600';
  if (scoreValue >= 2) return 'from-amber-400 to-amber-600';
  if (scoreValue >= 1) return 'from-orange-400 to-orange-600';
  return 'from-slate-300 to-slate-400';
};

/**
 * Get lead quality label based on score
 */
export const getLeadQualityLabel = (score: number | null | undefined): string => {
  const scoreValue = score || 0;
  if (scoreValue >= 5) return 'Hot';
  if (scoreValue >= 4) return 'Warm';
  if (scoreValue >= 3) return 'Interested';
  if (scoreValue >= 2) return 'Cool';
  if (scoreValue >= 1) return 'Cold';
  return 'Unrated';
};

/**
 * Format address for display
 */
export const formatAddress = (
  address?: string | null,
  postcode?: string | null
): string => {
  if (address && postcode) {
    const normalizedAddress = address.replace(/\s+/g, '').toLowerCase();
    const normalizedPostcode = postcode.replace(/\s+/g, '').toLowerCase();

    if (normalizedAddress.endsWith(normalizedPostcode)) {
      return address;
    }

    return `${address}, ${postcode}`;
  }
  return address || postcode || '';
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (!text || text.length <= maxLength) return text || '';
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format date for display
 */
export const formatDate = (date: string | Date): string => {
  const dateObj = new Date(date);
  const now = new Date();
  const isCurrentYear = dateObj.getFullYear() === now.getFullYear();
  
  return dateObj.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: isCurrentYear ? undefined : 'numeric',
  });
};

/**
 * Calculate days in current stage
 */
export const calculateDaysInStage = (
  stageUpdatedAt?: string | Date | null
): number => {
  if (!stageUpdatedAt) return 0;
  const updated = new Date(stageUpdatedAt);
  const now = new Date();
  return Math.floor((now.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Get stage duration label
 */
export const getStageDurationLabel = (days: number): string => {
  if (days === 0) return 'New today';
  if (days === 1) return '1 day';
  if (days < 7) return `${days} days`;
  if (days < 14) return '1 week';
  if (days < 30) return `${Math.floor(days / 7)} weeks`;
  if (days < 60) return '1 month';
  return `${Math.floor(days / 30)} months`;
};
